import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../lib/firebase';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import Navbar from '../components/ui/Navbar';

import { 
  Briefcase, Upload, Save, Building2, User, 
  CheckCircle2, Search, Fingerprint, Calendar, Clock, MapPin, 
  UserCircle, Phone, Loader2, Info, Camera, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const ProfileTech = () => {
  const navigate = useNavigate();
  const { user, userProfile, authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [dbFunctions, setDbFunctions] = useState([]);
  const [searchArea, setSearchArea] = useState('');
  
  const [errors, setErrors] = useState({});
  const [validating, setValidating] = useState({});

  const [formData, setFormData] = useState({ 
    technicianName: '', 
    technicianDocument: '', 
    technicianPhone: '', 
    bio: '', 
    areas: [] 
  });

  const [techAddress, setTechAddress] = useState({
    cep: '', street: '', number: '', complement: '', neighborhood: '', city: '', state: ''
  });

  useEffect(() => {
    const fetchFunctions = async () => {
      try {
        const snap = await getDocs(collection(db, "functions"));
        setDbFunctions(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(f => f.active !== false).sort((a,b) => a.name.localeCompare(b.name)));
      } catch (e) { console.error(e); }
    };
    fetchFunctions();
  }, []);

  const maskPhone = (v) => v.replace(/\D/g, "").substring(0, 11).replace(/^(\d{2})(\d{5})(\d{4})/, "($1) $2-$3").replace(/^(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3");
  const maskCep = (v) => v.replace(/\D/g, "").substring(0, 8).replace(/(\d{5})(\d)/, "$1-$2");
  const maskCpfCnpj = (v) => {
    if (!v) return "";
    let r = v.replace(/\D/g, "").substring(0, 14);
    if (r.length <= 11) return r.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    return r.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  };

  useEffect(() => {
    if (userProfile) {
      const pData = userProfile.professionalData || {};
      setFormData({
        technicianName: pData.name || userProfile.technicianName || userProfile.name || '',
        technicianDocument: pData.document || userProfile.technicianDocument || userProfile.cpfCnpj || '',
        technicianPhone: pData.phone ? maskPhone(pData.phone) : userProfile.phone ? maskPhone(userProfile.phone) : '',
        bio: pData.bio || userProfile.bio || '',
        areas: pData.areas || userProfile.areas || []
      });
      setTechAddress(pData.address || userProfile.technicianAddress || {
        cep: '', street: '', number: '', complement: '', neighborhood: '', city: '', state: ''
      });
      setPhotoPreview(pData.photoUrl || userProfile.technicianPhotoUrl || userProfile.photoURL || null);
    }
  }, [userProfile]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Formato inválido! Use apenas PNG, JPG ou JPEG.");
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleCepBlur = async () => {
    const cleanCep = techAddress.cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) {
      setErrors(p => ({ ...p, cep: true }));
      return;
    }
    setValidating(p => ({ ...p, cep: true }));
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await res.json();
      if (data.erro) {
        setErrors(p => ({ ...p, cep: true }));
        toast.error("CEP não localizado!");
      } else {
        setErrors(p => ({ ...p, cep: false }));
        setTechAddress(prev => ({
          ...prev, 
          street: data.logradouro.toUpperCase(), 
          neighborhood: data.bairro ? data.bairro.toUpperCase() : 'CENTRO', 
          city: data.localidade.toUpperCase(), 
          state: data.uf.toUpperCase()
        }));
      }
    } catch (e) { toast.error("Erro na busca do CEP."); }
    finally { setValidating(p => ({ ...p, cep: false })); }
  };

  const handleUpdateTech = async () => {
    if (!photoPreview) return toast.error("A foto/logo profissional é obrigatória!");
    if (errors.cep) return toast.error("Corrija o CEP antes de salvar!");
    if (formData.areas.length === 0) return toast.error("Selecione ao menos uma especialidade!");
    
    setLoading(true);
    try {
      let photoUrl = photoPreview;
      if (photoFile) {
        const photoRef = ref(storage, `tech-photos/${user.uid}`);
        await uploadBytes(photoRef, photoFile);
        photoUrl = await getDownloadURL(photoRef);
      }

      const updatePayload = {
        technicianName: formData.technicianName,
        bio: formData.bio,
        areas: formData.areas,
        technicianPhotoUrl: photoUrl,
        technicianAddress: techAddress,
        professionalData: {
          ...userProfile.professionalData,
          name: formData.technicianName,
          phone: formData.technicianPhone.replace(/\D/g, ""),
          bio: formData.bio,
          areas: formData.areas,
          address: techAddress,
          photoUrl: photoUrl
        },
        updatedAt: new Date().toISOString()
      };

      await updateDoc(doc(db, 'users', user.uid), updatePayload);
      toast.success('Perfil profissional atualizado!');
    } catch (e) { 
      console.error(e);
      toast.error('Erro ao salvar dados.'); 
    } finally { setLoading(false); }
  };

  const getStatusInfo = () => {
    const s = (userProfile?.technicianStatus || 'pending').toLowerCase();
    if (s === 'approved') return { label: 'Ativo', color: 'bg-green-500' };
    return { label: 'Em Análise', color: 'bg-orange-400' };
  };

  if (authLoading) return null;

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 text-left">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 pt-32 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        
        {/* SIDEBAR */}
        <div className="lg:col-span-3 space-y-6">
            <div className="space-y-2">
                <button onClick={() => navigate('/profile')} className="w-full h-14 flex items-center px-6 rounded-2xl bg-white text-slate-400 hover:text-blue-600 font-black uppercase italic text-[10px] gap-4 border border-slate-100 transition-all">
                    <UserCircle size={18} /> Perfil Pessoal
                </button>
                <button onClick={() => navigate('/profile-tech')} className="w-full h-14 flex items-center px-6 rounded-2xl bg-orange-600 text-white font-black uppercase italic text-[10px] gap-4 shadow-xl">
                    <Briefcase size={18} /> Perfil Profissional
                </button>
            </div>

            <Card className="rounded-[2rem] border-none shadow-xl bg-slate-900 text-white p-6 space-y-4">
                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                    <div className="flex items-center gap-2"><Fingerprint className="w-4 h-4 text-orange-400" /><span className="text-[9px] font-black uppercase">Conta</span></div>
                    <Badge className="bg-orange-600 text-white border-none font-black italic uppercase text-[8px]">Técnico</Badge>
                </div>
                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                    <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-green-400" /><span className="text-[9px] font-black uppercase">Status</span></div>
                    <Badge className={`${getStatusInfo().color} text-white border-none font-black italic uppercase text-[8px]`}>{getStatusInfo().label}</Badge>
                </div>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-400" /><span className="text-[9px] font-black uppercase">Desde</span></div>
                    <span className="text-[10px] font-black italic uppercase">{userProfile?.createdAt ? new Date(userProfile.createdAt).toLocaleDateString() : '---'}</span>
                </div>
            </Card>
        </div>

        {/* CONTEÚDO PROFISSIONAL */}
        <div className="lg:col-span-9 space-y-8">
          <Card className="rounded-[2.5rem] border-none shadow-2xl bg-white border-t-8 border-orange-500 p-8 md:p-10">
            <CardContent className="p-0 space-y-10">
              
              <div className="flex items-center gap-6 bg-orange-50/50 p-6 rounded-[2rem] border border-orange-100">
                <div className="relative w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-lg bg-white group shrink-0">
                    {photoPreview ? <img src={photoPreview} className="w-full h-full object-cover" alt="" /> : <Building2 className="w-8 h-8 m-auto mt-8 text-slate-200" />}
                    <label htmlFor="tech-photo" className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                        <Camera className="text-white w-6 h-6" />
                        <input id="tech-photo" type="file" accept=".jpg,.jpeg,.png" className="hidden" onChange={handlePhotoChange} />
                    </label>
                </div>
                <div className="text-left flex-1">
                  <h4 className="font-black text-orange-600 uppercase italic text-sm mb-1">Identidade Profissional</h4>
                  <p className="text-[9px] font-bold text-slate-400 uppercase italic text-left">Foto ou Logo é obrigatória para exibição no mapa.</p>
                </div>
              </div>

              <div className="pt-2">
                <Button onClick={handleUpdateTech} disabled={loading || errors.cep} className="w-full h-16 bg-orange-600 hover:bg-orange-700 text-white font-black rounded-2xl uppercase italic text-xs shadow-xl active:scale-95 transition-all">
                  {loading ? <Loader2 className="animate-spin mr-3" /> : <Save className="mr-3" />} SALVAR DADOS PROFISSIONAIS
                </Button>
              </div>

              <div className="grid md:grid-cols-2 gap-6 text-left">
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase ml-2 text-slate-400 italic">Nome Fantasia / Comercial</Label>
                  <Input value={formData.technicianName} onChange={e => setFormData({...formData, technicianName: e.target.value.toUpperCase()})} className="h-14 rounded-2xl bg-slate-50 border-none font-bold uppercase italic shadow-inner" />
                </div>
                
                <div className="space-y-2 text-left">
                  <Label className="text-[10px] font-black uppercase ml-2 text-blue-600 italic">Telefone Comercial</Label>
                  <Input value={formData.technicianPhone} onChange={e => setFormData({...formData, technicianPhone: maskPhone(e.target.value)})} className="h-14 rounded-2xl bg-blue-50/30 border-blue-100 border font-black text-blue-700 uppercase italic shadow-inner" />
                </div>

                <div className="space-y-2 col-span-full">
                  <div className="flex items-center justify-between pr-2">
                    <Label className="text-[10px] font-black uppercase ml-2 text-slate-400 italic">CPF ou CNPJ (Bloqueado)</Label>
                    <Badge variant="outline" className="text-[8px] border-orange-200 text-orange-600 font-black uppercase italic">Verificado</Badge>
                  </div>
                  <Input value={maskCpfCnpj(formData.technicianDocument)} disabled className="h-14 rounded-2xl bg-slate-100 border-none font-bold text-slate-400 italic cursor-not-allowed" />
                </div>
              </div>

              <div className="space-y-2 text-left">
                <Label className="text-[10px] font-black uppercase ml-2 text-orange-500 italic">Biografia e Diferenciais</Label>
                <Textarea value={formData.bio} onChange={e => setFormData({...formData, bio: e.target.value})} className="min-h-[150px] rounded-[2rem] bg-slate-50 border-none font-bold italic text-slate-700 shadow-inner p-6 text-sm" placeholder="Conte sobre suas qualificações..." />
              </div>

              {/* LOCALIZAÇÃO TÉCNICA COM REGRAS DE EDIÇÃO */}
              <div className="pt-6 border-t border-slate-100 space-y-6">
                <h3 className="font-black text-slate-800 uppercase italic text-xs flex items-center gap-2"><MapPin size={16} className="text-orange-600"/> Base de Atendimento</h3>
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-5 relative">
                    <Label className="text-[8px] font-black text-slate-400 uppercase ml-2 mb-1">CEP</Label>
                    <Input 
                        value={techAddress.cep} 
                        placeholder="00000-000" 
                        onBlur={handleCepBlur} 
                        onChange={e => setTechAddress({...techAddress, cep: maskCep(e.target.value)})} 
                        className={`h-14 rounded-2xl font-bold text-center shadow-inner border-2 transition-all ${errors.cep ? 'border-red-500 bg-red-50' : 'border-transparent bg-slate-50'}`} 
                    />
                    {validating.cep && <Loader2 className="absolute right-2 top-8 w-4 h-4 animate-spin text-orange-500" />}
                  </div>
                  <div className="col-span-7">
                    <Label className="text-[8px] font-black text-slate-400 uppercase ml-2 mb-1">Rua / Avenida</Label>
                    <Input value={techAddress.street} placeholder="Rua" onChange={e => setTechAddress({...techAddress, street: e.target.value.toUpperCase()})} className="h-14 rounded-2xl bg-slate-50 border-none font-bold px-6 shadow-inner uppercase" />
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-4">
                    <Label className="text-[8px] font-black text-slate-400 uppercase ml-2 mb-1">Número</Label>
                    <Input value={techAddress.number} placeholder="Nº" onChange={e => setTechAddress({...techAddress, number: e.target.value})} className="h-14 rounded-2xl bg-slate-50 border-none font-bold text-center shadow-inner" />
                  </div>
                  <div className="col-span-8">
                    <Label className="text-[8px] font-black text-slate-400 uppercase ml-2 mb-1">Complemento</Label>
                    <Input value={techAddress.complement} placeholder="Sala, Bloco, etc." onChange={e => setTechAddress({...techAddress, complement: e.target.value.toUpperCase()})} className="h-14 rounded-2xl bg-slate-50 border-none font-bold px-6 shadow-inner uppercase" />
                  </div>
                </div>
                
                {/* CAMPOS TRAVADOS (AUTO-CENTRO) */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[8px] font-black text-orange-600 uppercase ml-2 italic">Bairro (Automático)</Label>
                    <Input value={techAddress.neighborhood} readOnly className="h-14 rounded-2xl bg-slate-100 border-none font-bold px-6 text-slate-500 uppercase cursor-not-allowed italic" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[8px] font-black text-orange-600 uppercase ml-2 italic">Cidade/UF (Automático)</Label>
                    <div className="flex gap-2">
                        <Input value={techAddress.city} readOnly className="h-14 rounded-2xl bg-slate-100 border-none font-bold px-6 text-slate-500 uppercase cursor-not-allowed flex-1 italic" />
                        <Input value={techAddress.state} readOnly className="h-14 w-14 rounded-2xl bg-slate-100 border-none font-bold text-center text-slate-500 uppercase cursor-not-allowed italic" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-6 border-t border-slate-100 text-left">
                <div className="flex justify-between items-center ml-2">
                  <Label className="text-[10px] font-black uppercase text-orange-500 italic">Especialidades Profissionais</Label>
                  <div className="relative w-48">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                    <Input placeholder="Buscar área..." value={searchArea} onChange={e => setSearchArea(e.target.value)} className="h-9 pl-8 rounded-xl bg-slate-50 border-none text-[10px] font-bold shadow-inner" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[250px] overflow-y-auto p-2 custom-scrollbar bg-slate-50/50 rounded-[2rem] border border-slate-100">
                  {dbFunctions.filter(f => f.name.toLowerCase().includes(searchArea.toLowerCase())).map((f) => (
                    <div key={f.id} onClick={() => setFormData(p => ({ ...p, areas: p.areas.includes(f.name) ? p.areas.filter(a => a !== f.name) : [...p.areas, f.name] }))} 
                      className={`flex items-center justify-between p-4 rounded-2xl border-2 transition-all cursor-pointer ${formData.areas.includes(f.name) ? 'border-orange-500 bg-orange-50 shadow-sm' : 'border-white bg-white hover:bg-orange-50'}`}>
                      <span className={`font-black uppercase italic text-[11px] ${formData.areas.includes(f.name) ? 'text-orange-600' : 'text-slate-400'}`}>{f.name}</span>
                      {formData.areas.includes(f.name) && <CheckCircle2 className="w-5 h-5 text-orange-500" />}
                    </div>
                  ))}
                </div>
              </div>

            </CardContent>
          </Card>
        </div>
      </main>
      <style dangerouslySetInnerHTML={{ __html: `.custom-scrollbar::-webkit-scrollbar { width: 4px; } .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 10px; }` }} />
    </div>
  );
};

export default ProfileTech;